import { useRef, type KeyboardEvent, type ReactNode } from 'react';

/** One selectable segment: its stable value and the label a reader sees. */
export interface SegmentedControlOption<T extends string = string> {
  readonly value: T;
  readonly label: ReactNode;
}

export interface SegmentedControlProps<T extends string = string> {
  readonly options: readonly SegmentedControlOption<T>[];
  /** The currently selected value. */
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Accessible group name; required because the control has no visible label. */
  readonly 'aria-label': string;
  readonly className?: string;
  readonly disabled?: boolean;
}

/**
 * A reusable segmented control: a compact, mutually-exclusive choice between a few
 * short options (e.g. Design | Preview). Implemented as an ARIA `radiogroup` with
 * ROVING focus, so the whole control is one tab stop and the arrow keys (plus
 * Home/End) move the selection between segments, matching native radio semantics.
 * Purely token-styled to sit alongside the workspace tabs.
 */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
  disabled = false,
}: SegmentedControlProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex((option) => option.value === value);

  // Move selection (and focus) to the option at `index`, wrapping around the ends
  // so arrow navigation is continuous like a native radiogroup.
  const focusAndSelect = (index: number): void => {
    const count = options.length;
    if (count === 0) return;
    const wrapped = ((index % count) + count) % count;
    const option = options[wrapped];
    if (option === undefined) return;
    onChange(option.value);
    refs.current[wrapped]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        focusAndSelect(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        focusAndSelect(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAndSelect(0);
        break;
      case 'End':
        event.preventDefault();
        focusAndSelect(options.length - 1);
        break;
      default:
        break;
    }
  };

  const classes = ['nuff-segmented', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="radiogroup" aria-label={ariaLabel}>
      {options.map((option, index) => {
        const selected = option.value === value;
        // Roving tabindex: only the selected segment is tabbable (or the first, if
        // nothing is selected yet), so the group is a single, predictable tab stop.
        const tabbable = selected || (selectedIndex === -1 && index === 0);
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={tabbable ? 0 : -1}
            disabled={disabled}
            className={`nuff-segmented__option${selected ? ' nuff-segmented__option--active' : ''}`}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
